import { useState, useEffect } from 'react';
import { useTrackCTA } from '../../lib/useTrackCTA';
import { pushToDataLayer } from '../../lib/analytics';

/**
 * Injects OG / Twitter / description meta tags + updates document.title for
 * the currently-loaded article. Cleans up on unmount so navigating away
 * doesn't leak stale tags into other pages.
 */
export function useArticleMetaTags(article) {
  useEffect(() => {
    if (!article) return;
    const ogData = article.og_data || {};
    const ogCard = `${process.env.REACT_APP_BACKEND_URL}/api/og/article/${article.slug}`;
    document.title = article.meta_title || `${article.title} | NinjAthlete`;

    const metaTags = [
      { property: 'og:title', content: ogData.og_title || article.meta_title || article.title },
      { property: 'og:description', content: ogData.og_description || article.meta_description || article.excerpt || '' },
      { property: 'og:image', content: ogCard },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: ogData.og_url || `https://ninjathlete.com/blogs/article/${article.slug}` },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: ogData.twitter_title || article.meta_title || article.title },
      { name: 'twitter:description', content: (ogData.twitter_description || article.meta_description || '').substring(0, 200) },
      { name: 'twitter:image', content: ogCard },
      { name: 'description', content: article.meta_description || article.excerpt || '' },
    ];

    const injected = [];
    metaTags.forEach((tag) => {
      const attr = tag.property ? 'property' : 'name';
      const val = tag.property || tag.name;
      let el = document.querySelector(`meta[${attr}="${val}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, val);
        document.head.appendChild(el);
        injected.push(el);
      }
      el.setAttribute('content', tag.content);
    });

    return () => {
      injected.forEach((el) => el.remove());
      document.title = 'NinjAthlete';
    };
  }, [article]);
}

/**
 * Handles popup/CTA interactions inside article content:
 *   - clicks on `[data-popup-trigger]` open the HYROX program popup
 *   - clicks on `.faq-question` toggle adjacent FAQ accordion
 *   - clicks on `[data-cta-id]` inside `.article-content` fire analytics
 */
export function useArticlePopupTriggers(article, contentRef, setShowPopup) {
  const trackCta = useTrackCTA();

  // popup-trigger clicks
  useEffect(() => {
    if (!article || !contentRef.current) return;
    const handleClick = (e) => {
      const trigger = e.target.closest('[data-popup-trigger]');
      if (trigger) {
        e.preventDefault();
        sessionStorage.removeItem('hyrox_popup_dismissed');
        setShowPopup(true);
      }
    };
    const el = contentRef.current;
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, [article, contentRef, setShowPopup]);

  // FAQ accordion
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const handleFaqClick = (e) => {
      const btn = e.target.closest('.faq-question');
      if (btn && btn.parentElement) {
        btn.parentElement.classList.toggle('active');
      }
    };
    el.addEventListener('click', handleFaqClick);
    return () => el.removeEventListener('click', handleFaqClick);
  }, [article, contentRef]);

  // CTA analytics — listens globally so it covers split-CTA articles where
  // contentRef only wraps half of the article
  useEffect(() => {
    if (!article) return;
    const onClick = (e) => {
      const el = e.target.closest('[data-cta-id]');
      if (!el) return;
      if (!el.closest('.article-content')) return;
      const ctaId = el.getAttribute('data-cta-id');
      const href = el.getAttribute('href') || '';
      trackCta(ctaId, href);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [article, trackCta]);
}

/**
 * Wires inline lead-magnet forms embedded in article HTML
 * (`<form data-magnet-form="<magnet_id>">`) to POST /api/leads/dojo.
 * Uses document-level delegation since content ships via dangerouslySetInnerHTML.
 */
export function useInlineMagnetForms(article) {
  useEffect(() => {
    if (!article) return;
    const onSubmit = (e) => {
      const form = e.target.closest && e.target.closest('form[data-magnet-form]');
      if (!form) return;
      e.preventDefault();
      const input = form.querySelector('input[type=email]');
      const email = (input?.value || '').trim();
      if (!email || email.indexOf('@') < 1) { input?.focus(); return; }
      const btn = form.querySelector('button[type=submit]');
      const btnLabel = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/dojo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magnet_id: form.dataset.magnetForm,
          source: form.dataset.magnetSource || 'article-inline',
          first_name: (form.querySelector('input[type=text]')?.value || '').trim() || null,
        }),
      }).then((r) => {
        if (!r.ok) throw new Error('subscribe failed');
        pushToDataLayer('lead_magnet_signup', {
          magnet_id: form.dataset.magnetForm,
          lead_source: form.dataset.magnetSource || 'article-inline',
        });
        const ok = document.createElement('p');
        ok.setAttribute('data-testid', 'magnet-form-success');
        ok.style.cssText = 'margin:0;color:#7ee787;font-weight:600;font-size:1em;line-height:1.6;';
        ok.textContent = `✓ Check your inbox — the PDF is on its way to ${email}.`;
        form.replaceChildren(ok);
      }).catch(() => {
        if (btn) { btn.disabled = false; btn.textContent = btnLabel; }
        if (input) { input.style.borderColor = '#e94560'; }
      });
    };
    document.addEventListener('submit', onSubmit, true);
    return () => document.removeEventListener('submit', onSubmit, true);
  }, [article]);
}

/**
 * Wires the inline cadence step-up calculator (#naCadIn / #naCadGo / #naCadBody)
 * embedded in article HTML — its original <script> is stripped by DOMPurify.
 */
export function useCadenceCalculator(article) {
  useEffect(() => {
    if (!article) return;
    const render = () => {
      const input = document.getElementById('naCadIn');
      const body = document.getElementById('naCadBody');
      if (!input || !body) return;
      const c = parseInt(input.value, 10);
      if (!c || c < 120 || c > 220) return;
      const five = Math.round(c * 1.05);
      const ten = Math.round(c * 1.10);
      body.innerHTML =
        '<tr><td>Current</td><td>' + c + ' spm</td><td>Baseline</td>' +
        '<td>Measure over several runs, not one</td></tr>' +
        '<tr><td>Weeks 1\u20133</td><td>' + five + ' spm</td><td>+5%</td>' +
        '<td>Reduced knee energy absorption (Heiderscheit 2011)</td></tr>' +
        '<tr><td>Weeks 4\u20136</td><td>' + ten + ' spm</td><td>+10%</td>' +
        '<td>14% lower peak patellofemoral force (Lenhart 2014)</td></tr>';
    };
    const onClick = (e) => { if (e.target.closest && e.target.closest('#naCadGo')) render(); };
    const onChange = (e) => { if (e.target && e.target.id === 'naCadIn') render(); };
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('change', onChange);
    };
  }, [article]);
}

/**
 * Wires the recovery-economics article's interactive scorecard (filter buttons
 * re-sort the table via data attributes) and CRO click tracking for amenity
 * cards, calculator CTAs and the Bimini spotlight — all rendered from
 * sanitized article HTML, so no inline scripts survive.
 */
export function useRecoveryScorecard(article) {
  useEffect(() => {
    if (!article) return;
    const sortRows = (attr, desc) => {
      const tbody = document.getElementById('rec-score-body');
      if (!tbody) return;
      const rows = Array.from(tbody.querySelectorAll('tr'));
      rows.sort((a, b) => {
        const av = parseFloat(a.dataset[attr]) || 0;
        const bv = parseFloat(b.dataset[attr]) || 0;
        return desc ? bv - av : av - bv;
      });
      rows.forEach((r) => tbody.appendChild(r));
    };
    const FILTERS = {
      rank: ['rank', false],
      payback: ['payback', false],
      capex: ['capex', false],
      complexity: ['complexity', false],
      evidence: ['evidence', true],
    };
    const onClick = (e) => {
      const btn = e.target.closest && e.target.closest('[data-rec-filter]');
      if (btn) {
        const f = btn.dataset.recFilter;
        document.querySelectorAll('[data-rec-filter]').forEach((b) => b.classList.toggle('rec-filter-active', b === btn));
        if (FILTERS[f]) sortRows(FILTERS[f][0], FILTERS[f][1]);
        pushToDataLayer('scorecard_filter_used', { filter: f, article: article.slug });
        return;
      }
      const tracked = e.target.closest && e.target.closest('[data-track]');
      if (!tracked) return;
      const t = tracked.dataset.track;
      if (t === 'amenity-product') {
        pushToDataLayer('amenity_product_click', { amenity: tracked.dataset.amenity || '', article: article.slug });
      } else if (t === 'centr-product') {
        pushToDataLayer('centr_equipment_click', { product: tracked.dataset.centr || '', article: article.slug });
      } else if (t === 'calculator-start') {
        pushToDataLayer('calculator_start', { article: article.slug });
      } else if (t === 'bimini-product') {
        pushToDataLayer('bimini_product_click', { placement: 'article_spotlight', article: article.slug });
      }
    };
    document.addEventListener('click', onClick);
    // Videos injected via sanitized HTML don't honor autoplay/muted attributes
    // in all browsers — nudge playback explicitly once the content is in the DOM.
    const nudge = setInterval(() => {
      const vids = document.querySelectorAll('.nja-rec video');
      if (!vids.length) return;
      vids.forEach((v) => {
        v.muted = true;
        if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      });
      clearInterval(nudge);
    }, 800);
    return () => {
      document.removeEventListener('click', onClick);
      clearInterval(nudge);
    };
  }, [article]);
}

/**
 * Fires article_75_percent and article_complete dataLayer events once per
 * article view, based on scroll progress through the page.
 */
export function useScrollDepthTracking(article) {
  useEffect(() => {
    if (!article) return;
    const fired = { p75: false, complete: false };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = (window.scrollY / max) * 100;
      if (!fired.p75 && pct >= 75) {
        fired.p75 = true;
        pushToDataLayer('article_75_percent', { article: article.slug });
      }
      if (!fired.complete && pct >= 97) {
        fired.complete = true;
        pushToDataLayer('article_complete', { article: article.slug });
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [article]);
}

/**
 * Toggles a HYROX-only sticky CTA bar based on scroll position. Returns
 * `[visible, closed, dismiss]` for the bar's render logic.
 */
export function useHyroxStickyBar(article) {
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!article || article.category !== 'HYROX') return;
    if (sessionStorage.getItem('stickyBarClosed') === 'true') {
      setClosed(true);
      return;
    }
    const handleScroll = () => {
      if (closed) return;
      setVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article, closed]);

  const dismiss = () => {
    setClosed(true);
    setVisible(false);
    sessionStorage.setItem('stickyBarClosed', 'true');
  };

  return { visible, closed, dismiss };
}

/**
 * Hides <img> elements inside article content that fail to load (dead
 * hotlinks from legacy imports) so they never leave broken-icon gaps.
 */
export function useHideBrokenImages(article, contentRef) {
  useEffect(() => {
    if (!article || !contentRef.current) return;
    const root = contentRef.current;
    const handler = (e) => {
      if (e.target && e.target.tagName === 'IMG') e.target.style.display = 'none';
    };
    root.addEventListener('error', handler, true);
    root.querySelectorAll('img').forEach((img) => {
      if (img.complete && img.naturalWidth === 0 && img.src) img.style.display = 'none';
    });
    return () => root.removeEventListener('error', handler, true);
  }, [article, contentRef]);
}
